import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Download, Loader2, Copy, Save, Plus, Trash2, Calculator, TrendingUp, Tag, Target, CloudDownload } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const hrs = (v) => v == null ? '—' : `${Number(v).toFixed(1)} h`;
const pct = (v) => v == null ? '—' : `${Number(v).toFixed(1)}%`;
const card = { background: '#1A2433', border: '1px solid #2D3748' };
const inputStyle = { background: '#0F1923', color: '#E2E8F0', border: '1px solid #2D3748' };
const muted = { color: '#64748B' };

const Stat = ({ label, value, sub, accent, testid }) => (
  <div className="rounded-lg p-3" style={card} data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider" style={muted}>{label}</p>
    <p className="text-lg font-semibold" style={{ color: accent || '#fff' }}>{value}</p>
    {sub && <p className="text-[11px]" style={muted}>{sub}</p>}
  </div>
);

function HoursBreakdown({ h }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="sr-hours-breakdown">
      <Stat testid="sr-total-hours" label="Total hours worked" value={hrs(h.total_hours)} sub={`${h.workers} worker${h.workers === 1 ? '' : 's'}`} />
      <Stat testid="sr-billable-hours" label="Billable hours" value={hrs(h.billable_hours)} accent="#00C896" sub={`${pct(h.utilization_pct)} utilization`} />
      <Stat testid="sr-billable-job-hours" label="Billed via jobs" value={hrs(h.billable_job_hours)} sub={`~${money(h.job_revenue_estimate)} at job rates`} />
      <Stat testid="sr-billable-project-hours" label="Billed via projects" value={hrs(h.billable_project_hours)} sub="project time, no job" />
      <Stat testid="sr-overhead-hours" label="Overhead hours" value={hrs(h.overhead_hours)} accent="#FFA000" sub="overhead projects / non-billable jobs" />
      <Stat testid="sr-unassigned-hours" label="No project or job" value={hrs(h.unassigned_hours)} accent={h.unassigned_hours > 0 ? '#EF4444' : undefined} sub="counted as overhead" />
    </div>
  );
}

function ExpenseForm({ month, inputs, setInputs, onSaved, token }) {
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const setLine = (i, patch) => setInputs((s) => ({ ...s, lines: s.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) }));
  const addLine = () => setInputs((s) => ({ ...s, lines: [...s.lines, { key: `custom_${Date.now()}`, label: 'New expense', amount: 0 }] }));
  const removeLine = (i) => setInputs((s) => ({ ...s, lines: s.lines.filter((_, j) => j !== i) }));
  const num = (v) => (v === '' || v == null ? null : Number(v));

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        ...inputs,
        lines: inputs.lines.map((l) => ({ ...l, amount: Number(l.amount) || 0 })),
        target_margin_pct: Number(inputs.target_margin_pct) || 0,
        burden_pct: Number(inputs.burden_pct) || 0,
        current_rate: num(inputs.current_rate),
        labour_override: num(inputs.labour_override),
      };
      const { data } = await axios.put(`${API}/service-rate/inputs/${month}`, body, auth);
      setInputs(data);
      toast.success(`Saved figures for ${month}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setBusy(false); }
  };
  const pullQbo = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/service-rate/qbo-pnl`, { params: { month }, ...auth });
      if (!data.lines.length && !data.payroll_total) { toast.info(`QuickBooks shows no expenses for ${month}`); return; }
      setInputs((s) => ({ ...s, lines: data.lines.length ? data.lines : s.lines,
        labour_override: data.payroll_total ? data.payroll_total : s.labour_override }));
      toast.success(`Filled ${data.lines.length} expense lines from QuickBooks${data.payroll_total ? ` + payroll ${money(data.payroll_total)}` : ''} — review, then Save`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not read the QuickBooks Profit & Loss');
    } finally { setBusy(false); }
  };
  const copyPrev = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/service-rate/inputs/${month}/copy-previous`, {}, auth);
      setInputs(data);
      toast.success(`Copied figures from ${data.copied_from}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Nothing to copy');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl p-4 space-y-3" style={card} data-testid="sr-expense-form">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-white">Monthly figures from your accounting platform</p>
          <p className="text-[11px]" style={muted}>{inputs.saved ? 'Saved for this month' : 'Not saved yet — defaults shown'}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={pullQbo} disabled={busy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs" style={{ background: '#1E88E5', color: '#fff' }} data-testid="sr-pull-qbo-btn" title="Read this month's Profit & Loss from QuickBooks Online">
            <CloudDownload className="w-3.5 h-3.5" /> Pull from QuickBooks
          </button>
          <button onClick={copyPrev} disabled={busy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs" style={{ background: '#2D3748', color: '#fff' }} data-testid="sr-copy-previous-btn">
            <Copy className="w-3.5 h-3.5" /> Copy last month
          </button>
        </div>
      </div>
      <div className="space-y-1.5">
        {inputs.lines.map((l, i) => (
          <div key={l.key} className="flex items-center gap-2" data-testid={`sr-line-${l.key}`}>
            <input value={l.label} onChange={(e) => setLine(i, { label: e.target.value })} className="flex-1 px-2 py-1.5 rounded-lg text-xs" style={inputStyle} data-testid={`sr-line-label-${l.key}`} />
            <div className="relative">
              <span className="absolute left-2 top-1.5 text-xs" style={muted}>$</span>
              <input type="number" min="0" step="0.01" value={l.amount} onChange={(e) => setLine(i, { amount: e.target.value })} className="w-32 pl-5 pr-2 py-1.5 rounded-lg text-xs text-right" style={inputStyle} data-testid={`sr-line-amount-${l.key}`} />
            </div>
            <button onClick={() => removeLine(i)} className="p-1.5 rounded" style={{ color: '#64748B' }} title="Remove line" data-testid={`sr-line-remove-${l.key}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        <button onClick={addLine} className="flex items-center gap-1 text-xs mt-1" style={{ color: '#1E88E5' }} data-testid="sr-add-line-btn"><Plus className="w-3.5 h-3.5" /> Add expense line</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t" style={{ borderColor: '#2D3748' }}>
        <label className="text-[11px]" style={muted}>Payroll burden %
          <input type="number" min="0" step="0.5" value={inputs.burden_pct} onChange={(e) => setInputs((s) => ({ ...s, burden_pct: e.target.value }))} className="w-full mt-1 px-2 py-1.5 rounded-lg text-xs" style={inputStyle} data-testid="sr-burden-input" />
          <span className="block mt-0.5">CPP / EI / WCB / benefits on top of wages</span>
        </label>
        <label className="text-[11px]" style={muted}>Payroll from accounting ($)
          <input type="number" min="0" step="0.01" placeholder="blank = use HinesTime" value={inputs.labour_override ?? ''} onChange={(e) => setInputs((s) => ({ ...s, labour_override: e.target.value }))} className="w-full mt-1 px-2 py-1.5 rounded-lg text-xs" style={inputStyle} data-testid="sr-labour-override-input" />
          <span className="block mt-0.5">Overrides hours × pay rate when set</span>
        </label>
        <label className="text-[11px]" style={muted}>Target profit margin %
          <input type="number" min="0" max="99" step="0.5" value={inputs.target_margin_pct} onChange={(e) => setInputs((s) => ({ ...s, target_margin_pct: e.target.value }))} className="w-full mt-1 px-2 py-1.5 rounded-lg text-xs" style={inputStyle} data-testid="sr-margin-input" />
          <span className="block mt-0.5">Profit as a share of revenue</span>
        </label>
        <label className="text-[11px]" style={muted}>Current charged rate ($/h)
          <input type="number" min="0" step="0.01" placeholder="e.g. 95" value={inputs.current_rate ?? ''} onChange={(e) => setInputs((s) => ({ ...s, current_rate: e.target.value }))} className="w-full mt-1 px-2 py-1.5 rounded-lg text-xs" style={inputStyle} data-testid="sr-current-rate-input" />
          <span className="block mt-0.5">What you bill customers today</span>
        </label>
      </div>
      <div className="flex justify-end">
        <button onClick={save} disabled={busy} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium disabled:opacity-60" style={{ background: '#00C896', color: '#0B1321' }} data-testid="sr-save-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save & recalculate
        </button>
      </div>
    </div>
  );
}

function RateResults({ r, h }) {
  const gap = r.current_rate_gap_to_target;
  const gapColor = gap == null ? '#fff' : gap >= 0 ? '#00C896' : '#EF4444';
  return (
    <div className="space-y-3" data-testid="sr-results">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat testid="sr-total-cost" label="Total monthly cost" value={money(r.total_cost)} sub={`labour ${money(r.labour_cost_used)} + expenses ${money(r.expenses_total)}`} />
        <Stat testid="sr-break-even" label="Break-even rate" value={r.break_even_rate == null ? 'No billable hours' : `${money(r.break_even_rate)}/h`} accent="#FFA000" sub={`cost ÷ ${hrs(h.billable_hours)} billable`} />
        <Stat testid="sr-target-rate" label={`Target rate @ ${pct(r.target_margin_pct)} margin`} value={r.target_rate == null ? '—' : `${money(r.target_rate)}/h`} accent="#00C896" sub={r.required_revenue != null ? `needs ${money(r.required_revenue)} revenue` : ''} />
        <Stat testid="sr-current-vs-target" label="Current rate vs target" value={r.current_rate == null ? 'Enter current rate' : `${gap >= 0 ? '+' : ''}${money(gap)}/h`} accent={gapColor}
              sub={r.current_rate != null ? `${money(r.current_rate)}/h → ${pct(r.current_rate_margin_pct)} margin, ${money(r.current_rate_profit)} profit` : ''} />
      </div>
      {r.labour_source === 'override' && (
        <p className="text-[11px]" style={muted} data-testid="sr-labour-gap">
          Accounting payroll {money(r.labour_cost_used)} vs HinesTime calculation {money(r.labour_cost_calculated)} ({r.labour_gap >= 0 ? '+' : ''}{money(r.labour_gap)}).
        </p>
      )}
      {h.hours_without_rate > 0 && r.labour_source !== 'override' && (
        <p className="text-[11px]" style={{ color: '#FFA000' }} data-testid="sr-missing-rates">
          {hrs(h.hours_without_rate)} were worked by staff with no pay rate set — labour cost is understated. Set rates in Manage Users or enter payroll from accounting.
        </p>
      )}
      <div className="rounded-xl overflow-hidden" style={card}>
        <table className="w-full text-xs" data-testid="sr-cost-lines-table">
          <thead><tr className="text-left" style={muted}>
            <th className="px-3 py-2 font-medium uppercase tracking-wider">Cost of doing business</th>
            <th className="px-3 py-2 font-medium uppercase tracking-wider text-right">Per month</th>
            <th className="px-3 py-2 font-medium uppercase tracking-wider text-right">Per billable hour</th>
            <th className="px-3 py-2 font-medium uppercase tracking-wider text-right">Per hour worked</th>
          </tr></thead>
          <tbody>
            {r.lines.map((l) => (
              <tr key={l.key} style={{ borderTop: '1px solid #2D3748', color: '#E2E8F0' }} data-testid={`sr-cost-row-${l.key}`}>
                <td className="px-3 py-1.5">{l.label}</td>
                <td className="px-3 py-1.5 text-right">{money(l.amount)}</td>
                <td className="px-3 py-1.5 text-right">{money(l.per_billable_hour)}</td>
                <td className="px-3 py-1.5 text-right">{money(l.per_total_hour)}</td>
              </tr>
            ))}
            <tr style={{ borderTop: '1px solid #2D3748', color: '#fff', fontWeight: 600 }} data-testid="sr-cost-row-total">
              <td className="px-3 py-2">Total — break-even</td>
              <td className="px-3 py-2 text-right">{money(r.total_cost)}</td>
              <td className="px-3 py-2 text-right">{money(r.cost_per_billable_hour)}</td>
              <td className="px-3 py-2 text-right">{money(r.cost_per_total_hour)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-[11px]" style={muted}>
        Formula: target rate = (total monthly cost ÷ (1 − margin)) ÷ billable hours. Overhead hours ({pct(r.overhead_share_pct)} of time) are recovered inside the billable rate.
      </p>
    </div>
  );
}

function HoursTables({ h }) {
  const [view, setView] = useState('project');
  const rows = view === 'project' ? h.by_project : h.by_job;
  return (
    <div className="rounded-xl overflow-hidden" style={card} data-testid="sr-hours-tables">
      <div className="flex items-center gap-2 p-2 border-b" style={{ borderColor: '#2D3748' }}>
        {['project', 'job'].map((v) => (
          <button key={v} onClick={() => setView(v)} className="px-3 py-1 rounded-lg text-xs" style={{ background: view === v ? '#1E88E5' : '#0F1923', color: '#fff' }} data-testid={`sr-view-${v}`}>By {v}</button>
        ))}
        <span className="text-[11px] ml-auto" style={muted}>{rows.length} rows</span>
      </div>
      {rows.length === 0 ? <p className="p-4 text-xs" style={muted}>No hours logged this month.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left" style={muted}>
              {view === 'project' ? (<>
                <th className="px-3 py-2">Project</th><th className="px-3 py-2">Type</th>
                <th className="px-3 py-2 text-right">Hours</th><th className="px-3 py-2 text-right">On jobs</th>
                <th className="px-3 py-2 text-right">Project-only</th><th className="px-3 py-2 text-right">Labour cost</th>
              </>) : (<>
                <th className="px-3 py-2">Job</th><th className="px-3 py-2">Customer</th><th className="px-3 py-2">Project</th>
                <th className="px-3 py-2">Billable</th><th className="px-3 py-2 text-right">Hours</th>
                <th className="px-3 py-2 text-right">Labour cost</th><th className="px-3 py-2 text-right">Billed est.</th>
              </>)}
            </tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.project_id || r.job_id || i} style={{ borderTop: '1px solid #2D3748', color: '#E2E8F0' }} data-testid={`sr-row-${view}-${i}`}>
                  {view === 'project' ? (<>
                    <td className="px-3 py-1.5">{r.project_number ? `${r.project_number} — ` : ''}{r.name}</td>
                    <td className="px-3 py-1.5"><span className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: r.billing_type === 'overhead' ? '#FFA00033' : r.billing_type === 'unassigned' ? '#EF444433' : '#00C89633', color: r.billing_type === 'overhead' ? '#FFA000' : r.billing_type === 'unassigned' ? '#EF4444' : '#00C896' }}>{r.billing_type}</span></td>
                    <td className="px-3 py-1.5 text-right">{hrs(r.hours)}</td><td className="px-3 py-1.5 text-right">{hrs(r.job_hours)}</td>
                    <td className="px-3 py-1.5 text-right">{hrs(r.project_only_hours)}</td><td className="px-3 py-1.5 text-right">{money(r.labour_cost)}</td>
                  </>) : (<>
                    <td className="px-3 py-1.5">{r.job_number ? `${r.job_number} — ` : ''}{r.title}</td>
                    <td className="px-3 py-1.5">{r.customer_name || '—'}</td>
                    <td className="px-3 py-1.5">{r.project_number || r.project_name || '—'}</td>
                    <td className="px-3 py-1.5">{r.billable ? <span style={{ color: '#00C896' }}>yes</span> : <span style={{ color: '#FFA000' }}>no</span>}</td>
                    <td className="px-3 py-1.5 text-right">{hrs(r.hours)}</td><td className="px-3 py-1.5 text-right">{money(r.labour_cost)}</td>
                    <td className="px-3 py-1.5 text-right">{money(r.billed_estimate)}</td>
                  </>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function LabelRates({ rows }) {
  return (
    <div className="rounded-xl overflow-hidden" style={card} data-testid="sr-label-rates">
      <div className="flex items-center gap-2 p-2 border-b" style={{ borderColor: '#2D3748' }}>
        <Tag className="w-3.5 h-3.5" style={{ color: '#00C896' }} /><span className="text-xs text-white">Rate per service type (job label)</span>
        <span className="text-[11px] ml-auto" style={muted}>labour from the staff on those jobs · expenses spread per billable hour</span>
      </div>
      {rows.length === 0 ? <p className="p-4 text-xs" style={muted}>No billable job hours this month. Add labels to jobs (e.g. Plumbing, Electrical) to see a rate per service type.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left" style={muted}>
              <th className="px-3 py-2">Service type</th><th className="px-3 py-2 text-right">Jobs</th><th className="px-3 py-2 text-right">Billable h</th>
              <th className="px-3 py-2 text-right">Labour</th><th className="px-3 py-2 text-right">Expense share</th>
              <th className="px-3 py-2 text-right">Break-even</th><th className="px-3 py-2 text-right">Target</th><th className="px-3 py-2 text-right">Charging now</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.label_id || r.name} style={{ borderTop: '1px solid #2D3748', color: '#E2E8F0' }} data-testid={`sr-label-row-${r.label_id || 'none'}`}>
                  <td className="px-3 py-1.5">{r.name}</td>
                  <td className="px-3 py-1.5 text-right">{r.jobs}</td>
                  <td className="px-3 py-1.5 text-right">{hrs(r.billable_hours)}</td>
                  <td className="px-3 py-1.5 text-right">{money(r.labour_cost)}</td>
                  <td className="px-3 py-1.5 text-right">{money(r.expense_share)}</td>
                  <td className="px-3 py-1.5 text-right" style={{ color: '#FFA000' }}>{r.break_even_rate == null ? '—' : `${money(r.break_even_rate)}/h`}</td>
                  <td className="px-3 py-1.5 text-right" style={{ color: '#00C896' }}>{r.target_rate == null ? '—' : `${money(r.target_rate)}/h`}</td>
                  <td className="px-3 py-1.5 text-right" style={{ color: r.effective_gap_to_target == null ? '#E2E8F0' : r.effective_gap_to_target >= 0 ? '#00C896' : '#EF4444' }}>
                    {r.effective_rate == null ? '—' : `${money(r.effective_rate)}/h`}
                    {r.effective_gap_to_target != null && <span className="ml-1 text-[10px]">({r.effective_gap_to_target >= 0 ? '+' : ''}{money(r.effective_gap_to_target)})</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function GoalsBar({ token }) {
  const [goals, setGoals] = useState(null);
  const [busy, setBusy] = useState(false);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  useEffect(() => { axios.get(`${API}/service-rate/goals`, auth).then((r) => setGoals(r.data)).catch(() => setGoals(null)); }, [token]);
  if (!goals) return null;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/service-rate/goals`, { ...goals, target_utilization_pct: Number(goals.target_utilization_pct) || 0 }, auth);
      setGoals(data); toast.success('Goals saved — the Home dashboard card uses these');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save goals'); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl p-3 flex flex-wrap items-center gap-3" style={card} data-testid="sr-goals-bar">
      <Target className="w-4 h-4" style={{ color: '#1E88E5' }} />
      <span className="text-xs text-white">Home dashboard alerts</span>
      <label className="text-[11px] flex items-center gap-1.5" style={muted}>Target utilization %
        <input type="number" min="0" max="100" step="1" value={goals.target_utilization_pct} onChange={(e) => setGoals((g) => ({ ...g, target_utilization_pct: e.target.value }))} className="w-20 px-2 py-1 rounded-lg text-xs" style={inputStyle} data-testid="sr-goal-utilization-input" />
      </label>
      <label className="text-[11px] flex items-center gap-1.5" style={muted}>
        <input type="checkbox" checked={goals.alerts_enabled} onChange={(e) => setGoals((g) => ({ ...g, alerts_enabled: e.target.checked }))} data-testid="sr-goal-alerts-toggle" /> Show card on Home
      </label>
      <span className="text-[11px]" style={muted}>Rate goal = this month's target rate (or the latest saved month's figures)</span>
      <button onClick={save} disabled={busy} className="ml-auto px-3 py-1.5 rounded-lg text-xs" style={{ background: '#2D3748', color: '#fff' }} data-testid="sr-goals-save-btn">Save goals</button>
    </div>
  );
}

function TrendTable({ rows }) {
  return (
    <div className="rounded-xl overflow-hidden" style={card} data-testid="sr-trend">
      <div className="flex items-center gap-2 p-2 border-b" style={{ borderColor: '#2D3748' }}>
        <TrendingUp className="w-3.5 h-3.5" style={{ color: '#1E88E5' }} /><span className="text-xs text-white">12-month trend</span>
        <span className="text-[11px] ml-auto" style={muted}>months without saved figures show hours only</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left" style={muted}>
            <th className="px-3 py-2">Month</th><th className="px-3 py-2 text-right">Worked</th><th className="px-3 py-2 text-right">Billable</th>
            <th className="px-3 py-2 text-right">Utilization</th><th className="px-3 py-2 text-right">Total cost</th>
            <th className="px-3 py-2 text-right">Break-even</th><th className="px-3 py-2 text-right">Target</th><th className="px-3 py-2 text-right">Current</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.month} style={{ borderTop: '1px solid #2D3748', color: '#E2E8F0' }} data-testid={`sr-trend-${r.month}`}>
                <td className="px-3 py-1.5">{r.month}</td>
                <td className="px-3 py-1.5 text-right">{hrs(r.total_hours)}</td>
                <td className="px-3 py-1.5 text-right">{hrs(r.billable_hours)}</td>
                <td className="px-3 py-1.5 text-right">{pct(r.utilization_pct)}</td>
                <td className="px-3 py-1.5 text-right">{r.inputs_saved ? money(r.total_cost) : '—'}</td>
                <td className="px-3 py-1.5 text-right">{r.inputs_saved && r.break_even_rate != null ? money(r.break_even_rate) : '—'}</td>
                <td className="px-3 py-1.5 text-right" style={{ color: '#00C896' }}>{r.inputs_saved && r.target_rate != null ? money(r.target_rate) : '—'}</td>
                <td className="px-3 py-1.5 text-right">{r.current_rate != null ? money(r.current_rate) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function ServiceRatePane({ token, startDate }) {
  const [month, setMonth] = useState(() => (startDate || new Date().toISOString()).slice(0, 7));
  const [data, setData] = useState(null);
  const [inputs, setInputs] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(false);
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: t }] = await Promise.all([
        axios.get(`${API}/service-rate/calc`, { params: { month }, ...auth }),
        axios.get(`${API}/service-rate/trend`, { params: { months: 12, end: month }, ...auth }),
      ]);
      setData(c); setInputs(c.inputs); setTrend(t.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load service rate data');
    } finally { setLoading(false); }
  }, [month, auth]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const res = await axios.get(`${API}/service-rate/export.csv`, { params: { month }, responseType: 'blob', ...auth });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = `service-rate-${month}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: '#0F1923', border: '1px solid #2D3748' }} data-testid="service-rate-pane">
      <div className="flex flex-wrap items-center gap-3 p-3 border-b" style={{ borderColor: '#2D3748' }}>
        <Calculator className="w-4 h-4" style={{ color: '#00C896' }} />
        <div className="text-sm font-medium text-white">Service Rate Calculator</div>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="px-2 py-1.5 rounded-lg text-xs" style={inputStyle} data-testid="sr-month-input" />
        {loading && <Loader2 className="w-4 h-4 animate-spin" style={muted} />}
        <button onClick={exportCsv} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium" style={{ background: '#00C896', color: '#0B1321' }} data-testid="sr-export-csv-btn">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      {data && inputs ? (
        <div className="p-3 space-y-4">
          <HoursBreakdown h={data.hours} />
          <div className="grid lg:grid-cols-2 gap-4">
            <ExpenseForm month={month} inputs={inputs} setInputs={setInputs} onSaved={load} token={token} />
            <RateResults r={data.result} h={data.hours} />
          </div>
          <LabelRates rows={data.result.by_label || []} />
          <HoursTables h={data.hours} />
          <TrendTable rows={trend} />
          <GoalsBar token={token} />
        </div>
      ) : <div className="p-8 text-center text-xs" style={muted}>{loading ? 'Loading…' : 'No data'}</div>}
    </div>
  );
}
